export type CompanyProfileLocale = 'en' | 'fi' | 'sv';

export interface CompanyProfileUiCopy {
  /** Breadcrumb segment linking to the company hub (currently best employers). */
  breadcrumbCompanies: string;
  summaryHeading: string;
  dataTablesHeading: string;
  metricColumn: string;
  valueColumn: string;
  categoryColumn: string;
  countColumn: string;
  monthColumn: string;
  jobPostingsColumn: string;
  captions: {
    overview: string;
    employerScore: string;
    salary: string;
    workModel: string;
    roles: string;
    locations: string;
    languages: string;
    hiringByMonth: string;
    financials: string;
    registry: string;
  };
  trendGrowing: string;
  trendDeclining: string;
  trendStable: string;
  salaryDataHeading: string;
  compareSalariesCta: string;
  medianLabel: string;
  middle50Label: string;
  basedOnListings: string;
  /** Eyebrow label above the hiring/workforce chart stack (work model, trends, roles, locations). */
  hiringInsightsEyebrow: string;
  openRolesHeading: string;
  openRolesSub: string;
  seeAllJobs: string;
  viewJobCta: string;
  salaryNotSpecified: string;
  monthlyLabel: string;
  /** {count} placeholder — link shown under the job cards when more roles exist than are listed. */
  moreRolesLink: string;
  locationHeading: string;
  companyInfoHeading: string;
  businessId: string;
  status: string;
  founded: string;
  businessLine: string;
  website: string;
  /** Link to the company's hosted careers page (TEC-488), shown only when published. */
  careersPage: string;
  readMore: string;
  readLess: string;
  employees: string;
  foundedYearEst: string;
  noOpenRoles: string;
  /** {company} placeholder is replaced with the display name. */
  noOpenRolesNetworkPrompt: string;
  connectOnLinkedIn: string;
  preferDirectContact: string;
  workModelChartTitle: string;
  rolesChartTitle: string;
  roleLevelChartTitle: string;
  locationsChartTitle: string;
  languageChartTitle: string;
  /** Heading above the clickable top-roles links (distinct from rolesChartTitle, the chart above it). */
  topRolesLinksHeading: string;
  /** {location} placeholder — single link to the company's top hiring location's job hub. */
  alsoHiringInLabel: string;
  jobListingSingular: string;
  jobListingPlural: string;
  notFoundTitle: string;
  notFoundDescription: string;
  /** Employer is not in our CompaniesTable (e.g. Työmarkkinatori-only names). */
  notFoundOutsideCatalogTitle: string;
  notFoundOutsideCatalogDescription: string;
  /** Row exists in CompaniesTable but Algolia profile payload is missing. */
  notFoundProfilePendingTitle: string;
  notFoundProfilePendingDescription: string;
  foundedYearSuffix: string;
  /** Shown in place of the score badge/breakdown when a company has no employerScore yet. */
  employerScorePendingTitle: string;
  employerScorePendingDescription: string;
  /** Heading for the always-shown open-application card (distinct from the zero-results prompt inside the roles panel). */
  openApplicationHeading: string;
  /** {company} placeholder is replaced with the display name. */
  openApplicationIntro: string;
  /** {company} placeholder — explains the existing follow/watchlist button above this card. */
  openApplicationFollowStep: string;
  openApplicationTrackStep: string;
  openApplicationTrackCta: string;
  openApplicationGuideCta: string;
}

const EN: CompanyProfileUiCopy = {
  breadcrumbCompanies: 'Companies',
  summaryHeading: 'Employer overview',
  dataTablesHeading: 'Structured data for this employer',
  metricColumn: 'Metric',
  valueColumn: 'Value',
  categoryColumn: 'Category',
  countColumn: 'Job postings (sample)',
  monthColumn: 'Month',
  jobPostingsColumn: 'Job postings',
  captions: {
    overview: 'Company profile summary',
    employerScore: 'Employer score breakdown',
    salary: 'Salary statistics from job listings',
    workModel: 'Remote, hybrid, and onsite job postings',
    roles: 'Roles companies hired for (aggregated)',
    locations: 'Hiring locations (aggregated)',
    languages: 'Language requirements (aggregated)',
    hiringByMonth: 'Monthly job posting volume',
    financials: 'Reported financial figures (where available)',
    registry: 'Finnish business registry fields',
  },
  trendGrowing: 'Hiring growing',
  trendDeclining: 'Hiring declining',
  trendStable: 'Hiring stable',
  salaryDataHeading: 'Salary data',
  compareSalariesCta: 'Compare salaries by role across Finland',
  medianLabel: 'Median',
  middle50Label: 'Middle 50%',
  basedOnListings: 'Based on',
  hiringInsightsEyebrow: 'Hiring & workforce insights',
  openRolesHeading: 'Open roles',
  openRolesSub:
    'Open listings for this employer in Finland from the JobCrawls jobs index, newest first.',
  seeAllJobs: 'See all jobs',
  viewJobCta: 'View job',
  salaryNotSpecified: 'Not specified',
  monthlyLabel: 'Monthly',
  moreRolesLink: '+{count} more roles',
  locationHeading: 'Location',
  companyInfoHeading: 'Company information',
  businessId: 'Business ID',
  status: 'Status',
  founded: 'Registration date',
  businessLine: 'Business line',
  website: 'Website',
  careersPage: 'Careers page',
  readMore: 'Read more',
  readLess: 'Read less',
  employees: 'employees',
  foundedYearEst: 'Founded',
  noOpenRoles: 'No open roles at this time.',
  noOpenRolesNetworkPrompt:
    'Many Finnish employers hire through their network rather than public postings. Reach out to {company} directly:',
  connectOnLinkedIn: 'Connect on LinkedIn',
  preferDirectContact: 'Prefer to reach out directly?',
  workModelChartTitle: 'Work model',
  topRolesLinksHeading: 'Top roles',
  alsoHiringInLabel: 'Also hiring in {location}',
  rolesChartTitle: 'Roles hired for',
  roleLevelChartTitle: 'Experience levels hired',
  locationsChartTitle: 'Hiring locations',
  languageChartTitle: 'Language requirements',
  jobListingSingular: 'job listing',
  jobListingPlural: 'job listings',
  notFoundTitle: 'Employer not found',
  notFoundDescription:
    'This employer page could not be loaded. It may have been removed or the link is out of date.',
  notFoundOutsideCatalogTitle: 'We do not have this employer in our directory yet',
  notFoundOutsideCatalogDescription:
    'JobCrawls only builds detailed employer profiles for companies we track in our own directory. This name is not in that set yet — often the case for some Työmarkkinatori listings — so there is no company page to show.',
  notFoundProfilePendingTitle: 'Employer profile not ready yet',
  notFoundProfilePendingDescription:
    'This employer is listed in our directory, but the public profile page is not available yet. Try again later, or browse open roles from search.',
  foundedYearSuffix: ' est.',
  employerScorePendingTitle: 'Not yet rated',
  employerScorePendingDescription:
    "We haven't calculated an Employer Score for this company yet. Check back soon, or browse their open roles below.",
  openApplicationHeading: 'Open application',
  openApplicationIntro:
    "JobCrawls will make sending an open application to {company} easier, whether or not something is open right now:",
  openApplicationFollowStep:
    'Follow {company} with the button above and we will notify you the moment a matching role opens.',
  openApplicationTrackStep:
    'Log the application you send in the My Jobs tracker, so you always know exactly what you have already applied to.',
  openApplicationTrackCta: 'Open the My Jobs tracker',
  openApplicationGuideCta: "Read our guide to Finland's job-search obligation",
};

const FI: CompanyProfileUiCopy = {
  breadcrumbCompanies: 'Yritykset',
  summaryHeading: 'Työnantajan kuvaus',
  dataTablesHeading: 'Rakenteinen tieto työnantajasta',
  metricColumn: 'Tunnusluku',
  valueColumn: 'Arvo',
  categoryColumn: 'Luokka',
  countColumn: 'Ilmoitukset (otos)',
  monthColumn: 'Kuukausi',
  jobPostingsColumn: 'Ilmoitukset',
  captions: {
    overview: 'Yrityksen profiilin yhteenveto',
    employerScore: 'Työnantajapisteiden erittely',
    salary: 'Palkkatilastot ilmoituksista',
    workModel: 'Etä-, hybridi- ja lähityöilmoitukset',
    roles: 'Yleisimmät roolit (aggregaatti)',
    locations: 'Rekrytointisijainnit (aggregaatti)',
    languages: 'Kielivaatimukset (aggregaatti)',
    hiringByMonth: 'Kuukausittainen ilmoitusmäärä',
    financials: 'Talousluvut (kun saatavilla)',
    registry: 'YTJ / kaupparekisteri',
  },
  trendGrowing: 'Rekrytointi kasvussa',
  trendDeclining: 'Rekrytointi laskussa',
  trendStable: 'Rekrytointi tasaista',
  salaryDataHeading: 'Palkkatiedot',
  compareSalariesCta: 'Vertaile palkkoja ammateittain Suomessa',
  medianLabel: 'Mediaani',
  middle50Label: 'Keskimmäinen 50 %',
  basedOnListings: 'Yhteensä',
  hiringInsightsEyebrow: 'Rekrytointi ja henkilöstö',
  openRolesHeading: 'Avoimet työpaikat',
  openRolesSub:
    'Työnantajan avoimet ilmoitukset Suomessa JobCrawlsin työpaikkahausta, uusimmat ensin.',
  seeAllJobs: 'Kaikki työpaikat',
  viewJobCta: 'Näytä työpaikka',
  salaryNotSpecified: 'Ei tiedossa',
  monthlyLabel: 'Kuukausi',
  moreRolesLink: '+{count} lisää paikkaa',
  locationHeading: 'Sijainti',
  companyInfoHeading: 'Yritystiedot',
  businessId: 'Y-tunnus',
  status: 'Tila',
  founded: 'Rekisteröintipäivä',
  businessLine: 'Toimiala',
  website: 'Verkkosivusto',
  careersPage: 'Urasivu',
  readMore: 'Lue lisää',
  readLess: 'Näytä vähemmän',
  employees: 'työntekijää',
  foundedYearEst: 'Perustettu',
  noOpenRoles: 'Ei avoimia työpaikkoja juuri nyt.',
  noOpenRolesNetworkPrompt:
    'Moni suomalainen työnantaja rekrytoi verkostojensa kautta, ei pelkästään ilmoituksilla. Ota suoraan yhteyttä yritykseen {company}:',
  connectOnLinkedIn: 'Ota yhteyttä LinkedInissä',
  preferDirectContact: 'Haluatko ottaa suoraan yhteyttä?',
  workModelChartTitle: 'Työmalli',
  topRolesLinksHeading: 'Suosituimmat roolit',
  alsoHiringInLabel: 'Rekrytoi myös alueella {location}',
  rolesChartTitle: 'Yleisimmät roolit',
  roleLevelChartTitle: 'Kokemustasot',
  locationsChartTitle: 'Rekrytointisijainnit',
  languageChartTitle: 'Kielivaatimukset',
  jobListingSingular: 'ilmoitus',
  jobListingPlural: 'ilmoitusta',
  notFoundTitle: 'Työnantajaa ei löytynyt',
  notFoundDescription:
    'Yrityssivua ei voitu ladata. Se on ehkä poistettu tai linkki on vanhentunut.',
  notFoundOutsideCatalogTitle: 'Tätä työnantajaa ei ole vielä hakemistossamme',
  notFoundOutsideCatalogDescription:
    'JobCrawls tekee yrityssivun vain työnantajille, jotka ovat omassa yrityshakemistossamme. Tätä nimeä ei siellä vielä ole (näin käy osalle Työmarkkinatorin ilmoituksista), joten yrityssivua ei ole.',
  notFoundProfilePendingTitle: 'Yritysprofiili ei ole vielä valmis',
  notFoundProfilePendingDescription:
    'Työnantaja on hakemistossamme, mutta julkinen profiilisivu ei ole vielä käytössä. Kokeile myöhemmin uudelleen tai selaa avoimia työpaikkoja haussa.',
  foundedYearSuffix: '',
  employerScorePendingTitle: 'Ei vielä pisteytetty',
  employerScorePendingDescription:
    'Tälle yritykselle ei ole vielä laskettu työnantajapisteitä. Käy myöhemmin uudelleen tai selaa avoimia työpaikkoja alta.',
  openApplicationHeading: 'Avoin hakemus',
  openApplicationIntro:
    'Avoimen hakemuksen lähettäminen yritykselle {company} on JobCrawlsilla helppoa, oli sopivaa paikkaa juuri nyt auki tai ei:',
  openApplicationFollowStep:
    'Seuraa yritystä {company} yllä olevalla painikkeella, niin ilmoitamme heti kun sopiva paikka avautuu.',
  openApplicationTrackStep:
    'Merkitse lähettämäsi hakemus My Jobs -seurantaan, niin tiedät aina, mihin olet jo hakenut.',
  openApplicationTrackCta: 'Avaa My Jobs -seuranta',
  openApplicationGuideCta: 'Lue oppaamme työnhakuvelvoitteesta',
};

const SV: CompanyProfileUiCopy = {
  breadcrumbCompanies: 'Företag',
  summaryHeading: 'Arbetsgivaröversikt',
  dataTablesHeading: 'Strukturerad data om arbetsgivaren',
  metricColumn: 'Mått',
  valueColumn: 'Värde',
  categoryColumn: 'Kategori',
  countColumn: 'Annonser (urval)',
  monthColumn: 'Månad',
  jobPostingsColumn: 'Annonser',
  captions: {
    overview: 'Sammanfattning av företagsprofilen',
    employerScore: 'Arbetsgivarbetyg del för del',
    salary: 'Lönstatistik från annonser',
    workModel: 'Distans-, hybrid- och platsannonser',
    roles: 'Vanligaste roller (aggregerat)',
    locations: 'Rekryteringsorter (aggregerat)',
    languages: 'Språkkrav (aggregerat)',
    hiringByMonth: 'Annonser per månad',
    financials: 'Nyckeltal (när tillgängliga)',
    registry: 'Finskt företagsregister',
  },
  trendGrowing: 'Rekrytering ökar',
  trendDeclining: 'Rekrytering minskar',
  trendStable: 'Rekrytering stabil',
  salaryDataHeading: 'Lönedata',
  compareSalariesCta: 'Jämför löner per yrke i Finland',
  medianLabel: 'Median',
  middle50Label: 'Mellersta 50 %',
  basedOnListings: 'Baserat på',
  hiringInsightsEyebrow: 'Rekrytering och arbetsstyrka',
  openRolesHeading: 'Lediga tjänster',
  openRolesSub:
    'Öppna annonser för denna arbetsgivare i Finland från JobCrawls jobbindex, nyast först.',
  seeAllJobs: 'Alla jobb',
  viewJobCta: 'Visa jobbet',
  salaryNotSpecified: 'Ej angivet',
  monthlyLabel: 'Månad',
  moreRolesLink: '+{count} fler tjänster',
  locationHeading: 'Plats',
  companyInfoHeading: 'Företagsinformation',
  businessId: 'FO-nummer',
  status: 'Status',
  founded: 'Registreringsdatum',
  businessLine: 'Bransch',
  website: 'Webbplats',
  careersPage: 'Karriärsida',
  readMore: 'Läs mer',
  readLess: 'Visa mindre',
  employees: 'anställda',
  foundedYearEst: 'Grundat',
  noOpenRoles: 'Inga lediga tjänster just nu.',
  noOpenRolesNetworkPrompt:
    'Många finländska arbetsgivare rekryterar via sitt nätverk snarare än öppna annonser. Kontakta {company} direkt:',
  connectOnLinkedIn: 'Kontakta på LinkedIn',
  preferDirectContact: 'Vill du kontakta direkt?',
  workModelChartTitle: 'Arbetssätt',
  topRolesLinksHeading: 'Populära roller',
  alsoHiringInLabel: 'Anställer även i {location}',
  rolesChartTitle: 'Vanligaste roller',
  roleLevelChartTitle: 'Erfarenhetsnivåer',
  locationsChartTitle: 'Rekryteringsorter',
  languageChartTitle: 'Språkkrav',
  jobListingSingular: 'annons',
  jobListingPlural: 'annonser',
  notFoundTitle: 'Arbetsgivaren hittades inte',
  notFoundDescription:
    'Den här arbetsgivarsidan kunde inte laddas. Den kan ha tagits bort eller länken är inaktuell.',
  notFoundOutsideCatalogTitle: 'Den här arbetsgivaren finns inte i vår katalog ännu',
  notFoundOutsideCatalogDescription:
    'JobCrawls bygger detaljerade arbetsgivarprofiler bara för företag vi följer i vår egen katalog. Det här namnet finns inte där ännu — vanligt för vissa Työmarkkinatori-annonser — så det finns ingen företagssida att visa.',
  notFoundProfilePendingTitle: 'Arbetsgivarprofilen är inte klar ännu',
  notFoundProfilePendingDescription:
    'Arbetsgivaren finns i vår katalog, men den publika profilsidan är inte tillgänglig ännu. Försök igen senare, eller bläddra bland lediga jobb via sökningen.',
  foundedYearSuffix: '',
  employerScorePendingTitle: 'Inte betygsatt ännu',
  employerScorePendingDescription:
    'Vi har inte beräknat ett arbetsgivarbetyg för det här företaget ännu. Kom tillbaka senare, eller bläddra bland lediga jobb nedan.',
  openApplicationHeading: 'Öppen ansökan',
  openApplicationIntro:
    'JobCrawls gör det enklare att skicka en öppen ansökan till {company}, oavsett om något är ledigt just nu:',
  openApplicationFollowStep:
    'Följ {company} med knappen ovan, så meddelar vi dig så fort en passande tjänst öppnas.',
  openApplicationTrackStep:
    'Logga ansökan du skickar i My Jobs-verktyget, så håller du alltid koll på vad du redan har sökt.',
  openApplicationTrackCta: 'Öppna My Jobs-verktyget',
  openApplicationGuideCta: 'Läs vår guide om skyldigheten att söka arbete',
};

export function getCompanyProfileUiCopy(locale: string): CompanyProfileUiCopy {
  if (locale === 'fi') return FI;
  if (locale === 'sv') return SV;
  return EN;
}
